import { useState, useCallback, useMemo, useRef } from 'react';
import cn from 'bem-cn';

import { getCategoriesForBreakpoint, getCategoriesForDisplay } from '../../data/categories';
import moreIcon from '../UI/Icon/icons/more.svg';

import { ScreenSize, useGetScreenSize } from '../../hooks';

import { CategorySelectorItem } from './CategorySelectorItem';
import { CategorySelectorMenu } from './CategorySelectorMenu';
import { CategorySelectorProps } from './CategorySelector.types';

import './CategorySelector.scss';

const b = cn('category-selector');

export const CategorySelector = ({
    selectedCategories,
    onChangeCategories,
    size = 'normal',
    maxSelectedCategoriesCount = 9,
    disabled = false,
    centered = false,
    excludedCategories,
}: CategorySelectorProps) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const moreButtonRef = useRef<HTMLDivElement>(null);

    const screenSize = useGetScreenSize();
    const isMobile = screenSize === ScreenSize.Mobile;

    // Получаем категории для текущего брейкпоинта с учетом выбранных
    const displayedCategories = useMemo(
        () =>
            getCategoriesForBreakpoint(screenSize, selectedCategories).filter(
                category => !excludedCategories?.includes(category.id),
            ),
        [screenSize, selectedCategories, excludedCategories],
    );

    // Остальные категории для отображения идут в меню "Ещё"
    const allDisplayCategories = useMemo(
        () =>
            getCategoriesForDisplay(selectedCategories).filter(category => !excludedCategories?.includes(category.id)),
        [selectedCategories, excludedCategories],
    );
    const menuCategories = useMemo(() => {
        const displayedCategoryIds = new Set(displayedCategories.map(cat => cat.id));

        return allDisplayCategories.filter(cat => !displayedCategoryIds.has(cat.id));
    }, [allDisplayCategories, displayedCategories]);

    // Проверяем, достигнут ли лимит выбора категорий
    const isLimitReached = useMemo(() => {
        return maxSelectedCategoriesCount ? selectedCategories.length >= maxSelectedCategoriesCount : false;
    }, [maxSelectedCategoriesCount, selectedCategories.length]);

    const selectedMenuCategories = useMemo(
        () => menuCategories.filter(category => selectedCategories.includes(category.id)),
        [menuCategories, selectedCategories],
    );

    const moreButtonText = selectedMenuCategories.length ? `Ещё ${selectedMenuCategories.length}` : 'Ещё';

    const handleMenuClose = () => {
        setIsMenuOpen(false);
        document.body.style.overflow = 'initial';
    };

    const handleMenuOpen = () => {
        setIsMenuOpen(true);

        if (isMobile) {
            document.body.style.overflow = 'hidden';

            return;
        }

        // Скроллим до кнопки с небольшим смещением вверх на 100, чтобы красиво показать дропдаун
        if (moreButtonRef.current) {
            const rect = moreButtonRef.current?.getBoundingClientRect();
            const newTopPosition = window.scrollY + rect.top - 100;
            const scrollDifference = Math.abs(newTopPosition - window.scrollY);

            // Скроллим только если разница больше 100px
            if (scrollDifference > 100) {
                window.scrollTo({ top: newTopPosition, behavior: 'smooth' });
            }
        }
    };

    const handleMoreClick = () => {
        if (isMenuOpen) {
            handleMenuClose();

            return;
        }

        handleMenuOpen();
    };

    const handleCategoryToggle = useCallback(
        (categoryId: string) => {
            if (selectedCategories.includes(categoryId)) {
                onChangeCategories([]);
            } else {
                onChangeCategories([categoryId]);
            }
        },
        [selectedCategories, onChangeCategories],
    );

    const handleClearAllMenuCategories = useCallback(() => {
        const menuCategoryIds = new Set(menuCategories.map(cat => cat.id));
        const newSelectedCategories = selectedCategories.filter(categoryId => !menuCategoryIds.has(categoryId));

        onChangeCategories(newSelectedCategories);
    }, [selectedCategories, menuCategories, onChangeCategories]);

    const renderMoreButton = () => {
        if (menuCategories.length === 0) {
            return null;
        }

        const isMoreButtonChecked = selectedMenuCategories.length > 0;

        return (
            <div className={b('more-tag', { 'with-number': isMoreButtonChecked })}>
                <div onClick={handleMoreClick} ref={moreButtonRef}>
                    <CategorySelectorItem
                        name={moreButtonText}
                        image={moreIcon}
                        size={size}
                        checked={isMoreButtonChecked}
                        disabled={false}
                        onClick={handleMoreClick}
                    />
                </div>
                <CategorySelectorMenu
                    categories={menuCategories}
                    checkedCategories={selectedCategories}
                    isOpen={isMenuOpen}
                    onCategoryToggle={handleCategoryToggle}
                    onClose={handleMenuClose}
                    onClearAll={handleClearAllMenuCategories}
                    triggerRef={moreButtonRef}
                />
            </div>
        );
    };

    const renderCategories = () => {
        return displayedCategories.map(category => {
            const isChecked = selectedCategories.includes(category.id);

            return (
                <div className={b('category')} key={category.id}>
                    <CategorySelectorItem
                        name={category.name}
                        image={category.image}
                        video={category.video}
                        size={size}
                        checked={isChecked}
                        disabled={isLimitReached && !isChecked}
                        onClick={() => handleCategoryToggle(category.id)}
                    />
                </div>
            );
        });
    };

    if (disabled) {
        return (
            <div className={b({ size, disabled, centered })}>
                {allDisplayCategories.map(category => {
                    const isSelected = selectedCategories.includes(category.id);

                    if (!isSelected) {
                        return null;
                    }

                    return (
                        <div className={b('category')} key={category.id}>
                            <CategorySelectorItem
                                name={category.name}
                                image={category.image}
                                video={category.video}
                                size={size}
                                disabled
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <div className={b({ size, disabled, centered })}>
            {renderCategories()}
            {renderMoreButton()}
        </div>
    );
};
